import {
	ActiveBorderColor,
	DefaultBorderColor,
	Header,
} from '@dealstack/frontend/shared/components/AccountSettings/Header';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { useTranslationAuth } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { QrCodeEnrollment } from '@dealstack/frontend/shared/components/QrCodeEnrollment/QrCodeEnrollment';
import { Text, Button, Flex } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useState, useEffect, useRef } from 'react';
import {
	deleteAuthFactor,
	setup2FA,
	verifyChallenge2FA,
} from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { canChangeCredentials } from '@dealstack/frontend/shared/dataExchange/services/user.service';
import type { SectionEditMode } from '@dealstack/frontend/shared/components/AccountSettings/AccountSettings.types';
import {
	VERIFICATION_CODE_LENGTH,
	VerificationCode,
} from '@dealstack/frontend/shared/components/VerificationCode/VerificationCode';
import { GenericError } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { getBackendErrorCode } from '@dealstack/frontend/shared/dataExchange/services/backendCodedError';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { useSignOut } from '@dealstack/frontend/shared/mechanism/session/signOut';

interface Props extends SectionEditMode {
	canSetup2FA: boolean;
	existingAuthenticationFactorId?: string;
}

export const Manage2FA = ({
	canSetup2FA,
	existingAuthenticationFactorId,
	isEditModeEnabled,
	setIsEditModeEnabled,
}: Props) => {
	const tAuth = useTranslationAuth();
	const signOut = useSignOut();
	const [currentStep, setCurrentStep] = useState<'qr' | 'verify'>('qr');
	const [verificationCode, setVerificationCode] = useState('');
	const [verificationErrorMessage, setVerificationErrorMessage] = useState('');

	const isEnrolledFactorVerified = useRef(false);

	const getSetup2FARequest = useQuery({
		queryKey: [API_PATHS.AUTH_2FA_SETUP_POST],
		queryFn: () => setup2FA(API_PATHS.AUTH_2FA_SETUP_POST),
		enabled: isEditModeEnabled && !existingAuthenticationFactorId,
	});
	const getCanChangeCredentials = useQuery({
		queryKey: [API_PATHS.AUTH_CAN_CHANGE_CREDENTIALS_POST],
		queryFn: ({ signal }) =>
			canChangeCredentials(API_PATHS.AUTH_CAN_CHANGE_CREDENTIALS_POST, signal),
	});
	// On the mutation rather than the call, so it stays pending until the refetch is done:
	// the challenge is single-use and a second submit is refused with a 403.
	const verifyChallenge2FAMutation = useMutation({
		mutationFn: verifyChallenge2FA,
		onSuccess: async (response) => {
			if (response.payload?.isValid) {
				isEnrolledFactorVerified.current = true;
				await getCanChangeCredentials.refetch();
				showToastV2({
					title: tAuth('2FA.toast.success.title'),
					message: tAuth('2FA.toast.success.description'),
				});
				setIsEditModeEnabled(false);
			} else {
				setVerificationErrorMessage(tAuth('2FA.verify.error'));
			}
		},
		onError: () => {
			setVerificationErrorMessage(tAuth('2FA.verify.error'));
		},
	});

	const deleteAuthFactorMutation = useMutation({
		mutationFn: deleteAuthFactor,
	});

	// Turning 2FA off logs the user out, so where MFA is required AuthKit makes them enrol
	// again. On the mutation rather than the call, so it still runs if the page unmounts.
	const turnOffMutation = useMutation({
		mutationFn: deleteAuthFactor,
		onSuccess: async () => {
			showToastV2({
				title: tAuth('2FA.account_setting.manage.toast.turn_off'),
			});
			await signOut();
		},
	});

	const response = getSetup2FARequest?.data?.payload;

	const onSubmit = () => {
		if (verifyChallenge2FAMutation.isPending) {
			return;
		}
		setVerificationErrorMessage('');
		verifyChallenge2FAMutation.mutate({
			authenticationChallengeId: response?.authenticationChallenge.id ?? '',
			code: verificationCode,
		});
	};

	const deleteAuthFactorRequest = () => {
		if (!existingAuthenticationFactorId || turnOffMutation.isPending) {
			return;
		}
		setVerificationErrorMessage('');
		turnOffMutation.mutate(
			{
				authenticationFactorId: existingAuthenticationFactorId,
				totpCode: verificationCode,
			},
			{
				onError: (error) => {
					const code = getBackendErrorCode(error);
					if (code === ERROR_CODES.AUTH.CREDENTIALS[50013]) {
						setVerificationErrorMessage(tAuth('2FA.verify.missing_code'));
						return;
					}
					setVerificationErrorMessage(tAuth('2FA.verify.error'));
				},
			}
		);
	};

	const enrolledFactorId = response?.authenticationFactor.id;
	const isEnrolling = isEditModeEnabled && !existingAuthenticationFactorId && !!enrolledFactorId;

	useEffect(() => {
		if (!isEnrolling || !enrolledFactorId) {
			return;
		}
		isEnrolledFactorVerified.current = false;
		return () => {
			if (isEnrolledFactorVerified.current) {
				return;
			}
			deleteAuthFactorMutation.mutate({
				authenticationFactorId: enrolledFactorId,
				totpCode: undefined,
			});
		};
	}, [isEnrolling, enrolledFactorId]);

	const onHeaderClick = () => {
		setIsEditModeEnabled(!isEditModeEnabled);
	};

	if (
		isEditModeEnabled &&
		(existingAuthenticationFactorId ? false : getSetup2FARequest.isLoading)
	) {
		return <FullscreenLoader name="pageLoading" />;
	}

	const manage2FAButtonText = existingAuthenticationFactorId
		? tAuth('2FA.account_setting.manage.button')
		: tAuth('2FA.account_setting.setup');
	const buttonText = isEditModeEnabled
		? tAuth('2FA.account_setting.cancel')
		: manage2FAButtonText;
	const titleText =
		isEditModeEnabled && !existingAuthenticationFactorId
			? tAuth('2FA.account_setting.setup_auth')
			: tAuth('2FA.account_setting.title');
	const borderColor = existingAuthenticationFactorId ? defaultTheme.v3.colors.teal[8] : undefined;

	const renderTitle = () => {
		return (
			<div className="flex gap-2 items-center">
				<IconLock stroke={1} />
				<Text size="B6Bold" c="dark.8">
					{titleText}
				</Text>
			</div>
		);
	};

	const renderContentText = () => {
		const contentText = existingAuthenticationFactorId
			? tAuth('2FA.account_setting.manage.description')
			: tAuth('2FA.account_setting.description');
		if (!existingAuthenticationFactorId) {
			return (
				<Text size="B6Medium" c="dark.0">
					{contentText}
				</Text>
			);
		}
		return (
			<>
				<Text size="B6Bold" c="dark.0">
					{contentText}
				</Text>
				{!isEditModeEnabled && (
					<Text size="B6Medium" c="dark.0">
						{tAuth('2FA.account_setting.manage.detail')}
					</Text>
				)}
			</>
		);
	};

	const renderEditModeComponent = () => {
		if (existingAuthenticationFactorId) {
			return (
				<Flex direction="column" gap="1rem" w="100%" className="mt-4">
					<Text size="B6Bold" c="dark.8">
						{tAuth('2FA.account_setting.manage.turn_off_warning')}
					</Text>
					<Text size="B6Medium" c="dark.0">
						{tAuth('2FA.verify.description')}
					</Text>
					<VerificationCode
						errorMessage={verificationErrorMessage}
						value={verificationCode}
						onChange={setVerificationCode}
						onSubmit={deleteAuthFactorRequest}
						size="LARGE"
					/>
					<Button
						variant="tertiary"
						onClick={deleteAuthFactorRequest}
						disabled={verificationCode.length < VERIFICATION_CODE_LENGTH}
						loading={turnOffMutation.isPending}
						style={{ width: 'fit-content' }}
						data-testid={TEST_ELEMENT_IDS.ACCOUNT_SETTING_2FA_TURN_OFF_BUTTON}
					>
						{tAuth('2FA.account_setting.manage.turn_off')}
					</Button>
				</Flex>
			);
		}

		if (currentStep === 'qr') {
			if (
				!response?.authenticationFactor?.totp?.qrCode ||
				!response?.authenticationFactor?.totp?.secret
			) {
				return <GenericError />;
			}

			return (
				<Flex direction="column" gap="1rem" w="100%">
					<QrCodeEnrollment
						qrCode={response?.authenticationFactor?.totp?.qrCode}
						secret={response?.authenticationFactor?.totp?.secret}
					/>
					<Button
						data-testid={TEST_ELEMENT_IDS.ACCOUNT_SETTING_QR_CODE_VERIFY_BTN}
						variant="primary"
						className="w-full"
						onClick={() => setCurrentStep('verify')}
					>
						{tAuth('2FA.scan.button_text')}
					</Button>
				</Flex>
			);
		}

		return (
			<Flex direction="column" gap="1rem" w="100%">
				<VerificationCode
					errorMessage={verificationErrorMessage}
					value={verificationCode}
					onChange={setVerificationCode}
					onSubmit={onSubmit}
					size="LARGE"
				/>
				<Button
					variant="primary"
					className="w-full"
					onClick={onSubmit}
					loading={verifyChallenge2FAMutation.isPending}
					data-testid={TEST_ELEMENT_IDS.ACCOUNT_SETTING_2FA_SUBMIT_BUTTON}
				>
					{tAuth('2FA.verify.button_text')}
				</Button>
			</Flex>
		);
	};

	return (
		<div>
			<Header
				mainTitle={renderTitle()}
				action={buttonText}
				onClick={onHeaderClick}
				canPerformAction={canSetup2FA || !!existingAuthenticationFactorId}
				data-testid={TEST_ELEMENT_IDS.ACCOUNT_SETTING_2FA_HEADER_ACTION_BUTTON}
				isEditModeEnabled={isEditModeEnabled}
				borderColor={borderColor}
			/>
			<div
				className="p-4 border-solid border-r border-b border-l rounded-b-lg"
				style={{
					borderColor: isEditModeEnabled
						? ActiveBorderColor
						: borderColor ?? DefaultBorderColor,
				}}
			>
				{renderContentText()}

				{isEditModeEnabled && (
					<div className="flex flex-col gap-4">{renderEditModeComponent()}</div>
				)}
			</div>
		</div>
	);
};
